/**
 * TripDetailsPage — the premium trip hub.
 *
 * Modular by design so future "sections" (weather, flights, packing,
 * AI tips, budget, memories, scrapbook, YIR) can be added as drop-in
 * sub-components without touching the outer layout. Each section is a
 * self-contained component that receives the shared `trip` prop and
 * renders a Card. To add a new section, drop a new component into
 * `sections/` and import it below.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Calendar, Heart, Users, Camera, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, BACKEND_URL } from "@/config/api";

// ── Modular sections (extend by adding more into this array) ──────────
import TripHeroSection from "@/components/premium/trip-sections/TripHeroSection";
import TripCountdownSection from "@/components/premium/trip-sections/TripCountdownSection";
import TripMemoriesSection from "@/components/premium/trip-sections/TripMemoriesSection";
import TripFuturePlaceholders from "@/components/premium/trip-sections/TripFuturePlaceholders";

const SECTIONS = [
  { Component: TripHeroSection, key: "hero" },
  { Component: TripCountdownSection, key: "countdown" },
  { Component: TripMemoriesSection, key: "memories" },
  { Component: TripFuturePlaceholders, key: "future" },
];

export default function TripDetailsPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/items/${itemId}`);
        if (!cancelled) setTrip(res.data);
      } catch (_) {
        if (!cancelled) navigate("/dashboard", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [itemId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!trip) return null;

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="trip-details-page">
      <div className="sticky top-0 z-20 bg-background/85 backdrop-blur-md border-b border-border/60 px-4 py-3">
        <div className="flex items-center gap-3 max-w-2xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="trip-details-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-base font-semibold text-foreground truncate">{trip.title}</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {SECTIONS.map(({ Component, key }, i) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05, duration: 0.4 }}
          >
            <Component trip={trip} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// Re-export the small helpers so sections don't duplicate.
export { BACKEND_URL, API };
